import { gql } from '@apollo/client'
import { BodyText, Card, Chip, Heading } from '@farol-ds/react'
import classNames from 'classnames'

import { CheckoutButton } from '../../../checkout-button'
import { OneClickButton } from '../../../one-click-button'
import { BenefitsList } from '../benefits-list'
import { LockIcon } from '../lock-icon'
import { OfferBanner } from '../offer-banner'
import {
  NewOfferCard_OfferCardDataFragment,
  NewOfferCard_ViewerFragment,
} from './__generated__/offer-card.graphql'
import styles from './offer-card.module.scss'
import { OfferCardPrice } from './offer-card-price'

interface OfferCardProps {
  offerCardData: NewOfferCard_OfferCardDataFragment
  viewer: NewOfferCard_ViewerFragment
}

export const OfferCard = ({ offerCardData, viewer }: OfferCardProps) => {
  const { title, subtitle, offer, creditCard, buttons, icon, chip, banner, forceCheckout } =
    offerCardData
  const { isRecommendedOffer } = offer
  const { label: chipLabel, kind: chipKind } = chip

  const hasCreditCard = !!creditCard?.ccToken
  const kindButton = buttons?.[0]?.kind || (isRecommendedOffer ? 'primary' : 'secondary')

  return (
    <div
      className={classNames(styles.container, {
        [styles.recommendedContainer]: isRecommendedOffer,
      })}
    >
      <BodyText size="md" className={styles.recommendedText}>
        {isRecommendedOffer && 'Recomendado'}
      </BodyText>

      <Card className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerTitleContainer}>
            <div className={styles.headerTitle}>
              <LockIcon icon={icon} />
              <Heading size="md">{title}</Heading>
            </div>

            {chipLabel && (
              <Chip size="sm" kind={chipKind ?? 'tertiary'} className={styles.chip}>
                <span dangerouslySetInnerHTML={{ __html: chipLabel }} />
              </Chip>
            )}
          </div>

          <BodyText size="md">{subtitle}</BodyText>
        </div>

        <OfferCardPrice offerLockOffer={offer} />

        <div className={styles.paymentButtons}>
          <CheckoutButton kind={kindButton} offerLockCard={offerCardData} />
          {forceCheckout && hasCreditCard ? (
            <CheckoutButton kind={kindButton} offerLockCard={offerCardData} forceCheckout />
          ) : (
            !!viewer &&
            hasCreditCard && (
              <OneClickButton offerLockCard={offerCardData} viewer={viewer} kind={kindButton} />
            )
          )}
        </div>

        {banner && <OfferBanner offerLockBanner={banner} />}

        <div>
          <BenefitsList offerLockCard={offerCardData} />
        </div>
      </Card>
    </div>
  )
}

OfferCard.fragments = {
  offerCardData: gql`
    fragment NewOfferCard_offerCardData on OfferLockCard {
      title
      subtitle
      chip {
        label
        kind
      }
      banner {
        ...OfferBanner_offerLockBanner
      }
      icon {
        ...LockIcon_offerLockIcon
      }
      offer {
        isRecommendedOffer
        ...NewOfferCardPrice_offerLockOffer
      }
      buttons {
        label
        kind
      }
      forceCheckout
      ...NewBenefitsList_offerLockCard
      ...CheckoutButton_offerLockCard
      ...OneClickButton_offerLockCard
    }
    ${CheckoutButton.fragments.offerLockCard}
    ${OneClickButton.fragments.offerLockCard}
    ${OfferCardPrice.fragments.offerLockOffer}
    ${BenefitsList.fragments.offerLockCard}
    ${OfferBanner.fragments.offerLockBanner}
    ${LockIcon.fragments.icon}
  `,
  viewer: gql`
    fragment NewOfferCard_viewer on Profile {
      pid
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
